import {FC, useEffect, useRef, useState} from 'react';
import {useCan} from 'src/permissions/hooks';
import {Box, Button, Typography} from '@mui/material';
import {FormattedMessage, useIntl} from 'react-intl';
import {useParams} from 'react-router-dom';
import {pricingApi} from 'src/api';
import {useAppDispatch, useAppSelector} from 'src/app/hooks';
import {WeeklyMatrix} from 'src/components/pricing/WeeklyMatrix';
import {AMPInput} from 'src/components/common/AMPInput';
import {DayOfWeek, EnergyType, Month} from 'src/types/pricing';
import AMPModal from 'src/components/common/AMPModal';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {NotifyError, NotifySuccess} from 'src/utils/notification';
import {formatPrice} from 'src/utils/helper';
import {
    containerSx,
    currencySx,
    dayHeaderSx,
    daySubtitleSx,
    dayTitleSx,
    dayViewSx,
    defaultPriceInputSx,
    defaultPriceLabelSx,
    defaultPriceSectionSx,
    energyTypeCardSx,
    energyTypeHeaderSx,
    energyTypeSectionSx,
    energyTypesGridSx,
    headerSx,
    mainContentSx,
    monthCardSx,
    monthNameSx,
    monthsGridSx,
    monthSubtitleSx,
    priceInputContainerSx,
    seasonCardSx,
    seasonHeaderSx,
    seasonIconSx,
    seasonsSectionSx,
} from './DynamicPricing.sx';

const SEASON_COLORS = {
    winter: '#007fdc',
    spring: '#399646',
    summer: '#ff7d22',
    autumn: '#D92156',
};

const SEASONS = [
    {
        nameKey: 'seasonWinter',
        icon: '❄️',
        color: SEASON_COLORS.winter,
        months: [
            {key: Month.DECEMBER, nameKey: 'DECEMBER', subtitleKey: 'earlyWinter'},
            {key: Month.JANUARY, nameKey: 'JANUARY', subtitleKey: 'peakWinter'},
            {key: Month.FEBRUARY, nameKey: 'FEBRUARY', subtitleKey: 'lateWinter'},
        ],
    },
    {
        nameKey: 'seasonSpring',
        icon: '🌱',
        color: SEASON_COLORS.spring,
        months: [
            {key: Month.MARCH, nameKey: 'MARCH', subtitleKey: 'earlySpring'},
            {key: Month.APRIL, nameKey: 'APRIL', subtitleKey: 'midSpring'},
            {key: Month.MAY, nameKey: 'MAY', subtitleKey: 'lateSpring'},
        ],
    },
    {
        nameKey: 'seasonSummer',
        icon: '☀️',
        color: SEASON_COLORS.summer,
        months: [
            {key: Month.JUNE, nameKey: 'JUNE', subtitleKey: 'earlySummer'},
            {key: Month.JULY, nameKey: 'JULY', subtitleKey: 'peakSummer'},
            {key: Month.AUGUST, nameKey: 'AUGUST', subtitleKey: 'lateSummer'},
        ],
    },
    {
        nameKey: 'seasonAutumn',
        icon: '🍂',
        color: SEASON_COLORS.autumn,
        months: [
            {key: Month.SEPTEMBER, nameKey: 'SEPTEMBER', subtitleKey: 'earlyAutumn'},
            {key: Month.OCTOBER, nameKey: 'OCTOBER', subtitleKey: 'midAutumn'},
            {key: Month.NOVEMBER, nameKey: 'NOVEMBER', subtitleKey: 'lateAutumn'},
        ],
    },
];

const ENERGY_TYPES = [
    {key: EnergyType.REGULAR_NETWORK, nameKey: 'REGULAR_NETWORK', subtitleKey: 'gridPower'},
    {key: EnergyType.SOLAR, nameKey: 'SOLAR', subtitleKey: 'renewable'},
    {key: EnergyType.BATTERY_STORAGE, nameKey: 'BATTERY_STORAGE', subtitleKey: 'storedEnergy'},
];

export const DynamicPricing: FC = () => {
    const {locationId: _locationId, priceId} = useParams<{ locationId: string; priceId: string }>();
    const {activeBusiness} = useAppSelector((state) => state.common);
    const dispatch = useAppDispatch();
    const intl = useIntl();

    const [trigger, response] = pricingApi.useLazyGetPriceByIdQuery();
    const [updatePrice] = pricingApi.useUpdatePriceMutation();
    const [triggerPriceItems, priceItemsResponse] = pricingApi.useLazyFindPriceItemsByFiltersQuery();

    // Get the current month
    const getCurrentMonth = (): Month => {
        const monthIndex = new Date().getMonth(); // 0-11
        const monthMap: Month[] = [
            Month.JANUARY,
            Month.FEBRUARY,
            Month.MARCH,
            Month.APRIL,
            Month.MAY,
            Month.JUNE,
            Month.JULY,
            Month.AUGUST,
            Month.SEPTEMBER,
            Month.OCTOBER,
            Month.NOVEMBER,
            Month.DECEMBER,
        ];
        return monthMap[monthIndex];
    };

    const [selectedMonth, setSelectedMonth] = useState<Month>(getCurrentMonth());
    const [selectedEnergyType, setSelectedEnergyType] = useState<EnergyType>(EnergyType.REGULAR_NETWORK);
    const [newDefaultPrice, setNewDefaultPrice] = useState<string>('0');
    const [showPriceModal, setShowPriceModal] = useState<boolean>(false);
    const [isCopying, setIsCopying] = useState<boolean>(false);
    const dayViewRef = useRef<HTMLDivElement>(null);

    const canManagePricing = useCan('prices.update');
    const defaultPrice = response.data ? response.data.defaultPrice / 10000 : 0;
    void defaultPrice; // Reserved for future use

    const handleSetPriceForCells = async (cells: Array<{ day: DayOfWeek; hour: number }>, price: number) => {
        if (!priceId || !response.data) {
            return;
        }

        const businessId = activeBusiness?.id || response.data.location?.business?.id;

        if (!businessId) {
            return;
        }

        try {
            dispatch(preloaderChangeStatus(true));

            // Create price items from selected cells
            const items = cells.map((cell) => ({
                itemPrice: Math.round(price * 10000),
                energyType: selectedEnergyType,
                month: selectedMonth,
                dayOfWeek: cell.day,
                hourOfDay: cell.hour,
            }));

            await updatePrice({
                priceId: +priceId,
                data: {
                    defaultPrice: response.data.defaultPrice,
                    currency: response.data.currency,
                    businessId: businessId,
                    items: items,
                    cleaningItems: false,
                },
            }).unwrap();

            // Reload price items
            triggerPriceItems({
                data: {
                    priceId: +priceId,
                    month: selectedMonth,
                    energyType: selectedEnergyType,
                },
                params: {
                    size: 168, // 7 days × 24 hours
                },
            });

            NotifySuccess('Price updated successfully');
        } catch (error: any) {
            NotifyError(error?.data?.message || 'Failed to update price');
        } finally {
            dispatch(preloaderChangeStatus(false));
        }
    };

    const handleClearPriceForCells = async (cells: Array<{ day: DayOfWeek; hour: number }>) => {
        if (!priceId || !response.data) {
            return;
        }

        const businessId = activeBusiness?.id || response.data.location?.business?.id;

        if (!businessId) {
            return;
        }

        try {
            dispatch(preloaderChangeStatus(true));

            // Create items to delete from selected cells
            const itemsToDelete = cells.map((cell) => ({
                itemPrice: 0, // Price doesn't matter for deletion
                energyType: selectedEnergyType,
                month: selectedMonth,
                dayOfWeek: cell.day,
                hourOfDay: cell.hour,
            }));

            await updatePrice({
                priceId: +priceId,
                data: {
                    defaultPrice: response.data.defaultPrice,
                    currency: response.data.currency,
                    businessId: businessId,
                    itemsToDelete: itemsToDelete,
                    cleaningItems: true,
                },
            }).unwrap();

            // Reload price items
            triggerPriceItems({
                data: {
                    priceId: +priceId,
                    month: selectedMonth,
                    energyType: selectedEnergyType,
                },
                params: {
                    size: 168, // 7 days × 24 hours
                },
            });

            NotifySuccess('Prices cleared successfully');
        } catch (error: any) {
            NotifyError(error?.data?.message || 'Failed to clear prices');
        } finally {
            dispatch(preloaderChangeStatus(false));
        }
    };

    const handleCopyFromMonth = async (sourceMonth: Month) => {
        if (!priceId || !response.data) {
            return;
        }

        const businessId = activeBusiness?.id || response.data.location?.business?.id;

        if (!businessId) {
            return;
        }

        try {
            dispatch(preloaderChangeStatus(true));
            setIsCopying(true);

            // Fetch all price items from source month for all energy types
            const allEnergyTypes = [EnergyType.REGULAR_NETWORK, EnergyType.SOLAR, EnergyType.BATTERY_STORAGE];
            const sourceItems: Array<{
                itemPrice: number;
                energyType: EnergyType;
                month: Month;
                dayOfWeek: DayOfWeek;
                hourOfDay: number;
            }> = [];

            for (const energyType of allEnergyTypes) {
                const result = await triggerPriceItems({
                    data: {
                        priceId: +priceId,
                        month: sourceMonth,
                        energyType: energyType,
                    },
                    params: {
                        size: 168, // 7 days × 24 hours
                    },
                }).unwrap();

                if (result?.content) {
                    // Map source items to target month
                    result.content.forEach((item) => {
                        sourceItems.push({
                            itemPrice: item.itemPrice,
                            energyType: item.energyType,
                            month: selectedMonth, // Target month
                            dayOfWeek: item.dayOfWeek,
                            hourOfDay: item.hourOfDay,
                        });
                    });
                }
            }

            // First, clear all existing items in target month for all energy types
            const itemsToDelete: Array<{
                itemPrice: number;
                energyType: EnergyType;
                month: Month;
                dayOfWeek: DayOfWeek;
                hourOfDay: number;
            }> = [];

            const allDays: DayOfWeek[] = [
                DayOfWeek.MONDAY, DayOfWeek.TUESDAY, DayOfWeek.WEDNESDAY,
                DayOfWeek.THURSDAY, DayOfWeek.FRIDAY, DayOfWeek.SATURDAY, DayOfWeek.SUNDAY
            ];

            for (const energyType of allEnergyTypes) {
                for (const day of allDays) {
                    for (let hour = 0; hour < 24; hour++) {
                        itemsToDelete.push({
                            itemPrice: 0,
                            energyType,
                            month: selectedMonth,
                            dayOfWeek: day,
                            hourOfDay: hour,
                        });
                    }
                }
            }

            // Clear existing prices in the target month
            await updatePrice({
                priceId: +priceId,
                data: {
                    defaultPrice: response.data.defaultPrice,
                    currency: response.data.currency,
                    businessId: businessId,
                    itemsToDelete: itemsToDelete,
                    cleaningItems: true,
                },
            }).unwrap();

            // Now add the copied items (if any exist in source month)
            if (sourceItems.length > 0) {
                await updatePrice({
                    priceId: +priceId,
                    data: {
                        defaultPrice: response.data.defaultPrice,
                        currency: response.data.currency,
                        businessId: businessId,
                        items: sourceItems,
                        cleaningItems: false,
                    },
                }).unwrap();
            }

            // Wait a bit for cache invalidation to settle, then reload price items for current view
            await new Promise(resolve => setTimeout(resolve, 300));

            await triggerPriceItems({
                data: {
                    priceId: +priceId,
                    month: selectedMonth,
                    energyType: selectedEnergyType,
                },
                params: {
                    size: 168,
                },
            }).unwrap();

            NotifySuccess(intl.formatMessage({id: 'pricesCopiedSuccessfully'}));
        } catch (error: any) {
            NotifyError(error?.data?.message || 'Failed to copy prices');
        } finally {
            setIsCopying(false);
            dispatch(preloaderChangeStatus(false));
        }
    };

    useEffect(() => {
        if (priceId) {
            void trigger(+priceId);
        }
    }, [priceId, trigger]);

    // Load price items when a month or energy type changes
    useEffect(() => {
        if (priceId) {
            void triggerPriceItems({
                data: {
                    priceId: +priceId,
                    month: selectedMonth,
                    energyType: selectedEnergyType,
                },
                params: {
                    size: 168, // 7 days × 24 hours
                },
            });
        }
    }, [priceId, selectedMonth, selectedEnergyType, triggerPriceItems]);

    const handleMonthClick = (month: Month) => {
        setSelectedMonth(month);
        // Scroll to day view
        setTimeout(() => {
            dayViewRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
        }, 100);
    };

    const handleEnergyTypeClick = (energyType: EnergyType) => {
        setSelectedEnergyType(energyType);
    };

    const handleOpenPriceModal = () => {
        setNewDefaultPrice(formatPrice(response.data?.defaultPrice || 0));
        setShowPriceModal(true);
    };

    const handleClosePriceModal = () => {
        setShowPriceModal(false);
    };

    const handleUpdateDefaultPrice = async () => {
        if (!priceId || !response.data) {
            return;
        }

        const businessId = activeBusiness?.id || response.data.location?.business?.id;

        if (!businessId) {
            return;
        }

        // Validate price
        const priceNumber = parseFloat(newDefaultPrice);
        if (isNaN(priceNumber) || newDefaultPrice === '' || priceNumber < 0) {
            NotifyError('Please enter a valid price');
            return;
        }

        try {
            dispatch(preloaderChangeStatus(true));
            await updatePrice({
                priceId: +priceId,
                data: {
                    defaultPrice: Math.round(priceNumber * 10000),
                    currency: response.data.currency,
                    businessId: businessId,
                    items: [],
                },
            }).unwrap();

            setShowPriceModal(false);
        } catch (error: any) {
            NotifyError(error?.data?.message || 'Failed to update price');
        } finally {
            dispatch(preloaderChangeStatus(false));
        }
    };

    return (
        <>
            {response.isLoading && (
                <Typography variant="body1" sx={{padding: 3}}>
                    Loading...
                </Typography>
            )}

            {response.error && (
                <Typography variant="body1" color="error" sx={{padding: 3}}>
                    Error loading price data
                </Typography>
            )}

            {response.data && (
                <Box sx={containerSx}>
                    {/* Custom Header */}
                    <Box sx={headerSx}>
                        <Box>
                            <Typography variant="h4" sx={{fontWeight: 700, marginBottom: '8px'}}>
                                ⚡ {intl.formatMessage({id: 'dynamicPriceConfiguration'})}
                            </Typography>
                            <Typography sx={{opacity: 0.9, fontSize: '1rem', fontWeight: 400}}>
                                {intl.formatMessage({id: 'location'})}: {response.data.location.name}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={mainContentSx}>
                        {/* Default Price Section */}
                        <Box sx={defaultPriceSectionSx}>
                            <Typography component="label" sx={defaultPriceLabelSx}>
                                {intl.formatMessage({id: 'defaultPriceLabel'})}:
                            </Typography>
                            <Box sx={priceInputContainerSx}>
                                <Box
                                    component="input"
                                    type="text"
                                    value={formatPrice(response.data.defaultPrice)}
                                    readOnly
                                    onClick={canManagePricing ? handleOpenPriceModal : undefined}
                                    sx={{
                                        ...defaultPriceInputSx,
                                        cursor: canManagePricing ? 'pointer' : 'default',
                                        ...(!canManagePricing && {
                                            '&:hover': {
                                                backgroundColor: 'transparent',
                                                transform: 'none',
                                            },
                                        }),
                                    }}
                                />
                                <Typography sx={currencySx}>
                                    {response.data.currency}/kWh
                                </Typography>
                            </Box>
                        </Box>

                        {/* Seasons Section */}
                        <Box sx={seasonsSectionSx}>
                            {SEASONS.map((season) => {
                                // Light version of season color for selection
                                const getSeasonLightColor = (color: string) => {
                                    if (color === SEASON_COLORS.winter) return '#DEF0FC';
                                    if (color === SEASON_COLORS.spring) return '#E3F5E6';
                                    if (color === SEASON_COLORS.summer) return '#FFE8D6';
                                    if (color === SEASON_COLORS.autumn) return '#FFE3E9';
                                    return '#F5F5F7';
                                };

                                const selectedColor = getSeasonLightColor(season.color);

                                return (
                                    <Box key={season.nameKey} sx={seasonCardSx(season.color)}>
                                        <Typography sx={seasonHeaderSx}>
                                            <Box component="span" sx={seasonIconSx}>{season.icon}</Box>
                                            {intl.formatMessage({id: season.nameKey})}
                                        </Typography>
                                        <Box sx={monthsGridSx}>
                                            {season.months.map((month) => {
                                                const isSelected = selectedMonth === month.key;

                                                return (
                                                    <Box
                                                        key={month.key}
                                                        sx={{
                                                            ...monthCardSx,
                                                            '&:hover': {
                                                                transform: 'translateY(-4px)',
                                                                boxShadow: `0 8px 24px ${season.color}26`,
                                                                borderColor: season.color,
                                                                backgroundColor: selectedColor,
                                                            },
                                                            ...(isSelected && {
                                                                borderColor: season.color,
                                                                backgroundColor: selectedColor,
                                                                boxShadow: `0 4px 16px ${season.color}33`,
                                                            }),
                                                        }}
                                                        onClick={() => handleMonthClick(month.key)}
                                                    >
                                                        <Typography
                                                            sx={monthNameSx}>{intl.formatMessage({id: month.nameKey})}</Typography>
                                                        <Typography
                                                            sx={monthSubtitleSx}>{intl.formatMessage({id: month.subtitleKey})}</Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>

                        {/* Day View Section */}
                        <Box ref={dayViewRef} sx={dayViewSx}>
                            <Box sx={dayHeaderSx}>
                                <Typography sx={dayTitleSx}>
                                    {intl.formatMessage({id: SEASONS.flatMap(s => s.months).find(m => m.key === selectedMonth)?.nameKey || 'JANUARY'})}
                                </Typography>
                                <Typography
                                    sx={daySubtitleSx}>{intl.formatMessage({id: 'weeklyPricingMatrix'})}</Typography>
                            </Box>

                            {/* Energy Type Selector */}
                            <Box sx={energyTypeSectionSx}>
                                <Typography sx={energyTypeHeaderSx}>
                                    <Box component="span" sx={seasonIconSx}>⚡</Box>
                                    {intl.formatMessage({id: 'energySource'})}
                                </Typography>
                                <Box sx={energyTypesGridSx}>
                                    {ENERGY_TYPES.map((energyType) => {
                                        const isSelected = selectedEnergyType === energyType.key;
                                        let selectedColor = '#DEF0FC'; // Default blue for Regular Network
                                        let borderColor = '#007fdc';

                                        if (energyType.key === EnergyType.SOLAR) {
                                            selectedColor = '#FFE8D6'; // Light orange from summer
                                            borderColor = SEASON_COLORS.summer;
                                        } else if (energyType.key === EnergyType.BATTERY_STORAGE) {
                                            selectedColor = '#E3F5E6'; // Light green from spring
                                            borderColor = SEASON_COLORS.spring;
                                        }

                                        return (
                                            <Box
                                                key={energyType.key}
                                                sx={{
                                                    ...energyTypeCardSx,
                                                    '&:hover': {
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: `0 6px 20px ${borderColor}26`,
                                                        borderColor: borderColor,
                                                        backgroundColor: selectedColor,
                                                    },
                                                    ...(isSelected && {
                                                        borderColor: borderColor,
                                                        backgroundColor: selectedColor,
                                                        boxShadow: `0 4px 12px ${borderColor}33`,
                                                    }),
                                                }}
                                                onClick={() => handleEnergyTypeClick(energyType.key)}
                                            >
                                                <Typography sx={{
                                                    fontSize: '1rem',
                                                    fontWeight: 600,
                                                    color: '#32324D',
                                                    marginBottom: '2px'
                                                }}>
                                                    {intl.formatMessage({id: energyType.nameKey})}
                                                </Typography>
                                                <Typography
                                                    sx={{fontSize: '0.75rem', color: '#9090B0', fontWeight: 400}}>
                                                    {intl.formatMessage({id: energyType.subtitleKey})}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>

                            {/* Weekly Matrix */}
                            <WeeklyMatrix
                                priceItems={isCopying ? [] : (priceItemsResponse.data?.content || [])}
                                energyType={selectedEnergyType}
                                currency={response.data.currency}
                                currentMonth={selectedMonth}
                                onSetPrice={handleSetPriceForCells}
                                onClearPrice={handleClearPriceForCells}
                                onCopyFromMonth={canManagePricing ? handleCopyFromMonth : undefined}
                                readOnly={!canManagePricing}
                            />
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Default Price Edit Modal */}
            <AMPModal
                handleClose={handleClosePriceModal}
                open={showPriceModal}
                title={intl.formatMessage({id: 'editDefaultPrice'})}
                type="sm"
                fullWidth
            >
                {/* Full width below a desktop: the hour matrix needs every pixel it can get, and a
                    24px frame on a 390px screen is a sixth of the week it has to draw. */}
                <Box sx={{p: {xs: 0, md: 3}}}>
                    <Typography variant="body2" sx={{marginBottom: 2, color: '#666'}}>
                        {intl.formatMessage({id: 'currentPriceLabel'})}: {response.data ? formatPrice(response.data.defaultPrice) : '0'} {response.data?.currency}/kWh
                    </Typography>

                    <AMPInput
                        value={newDefaultPrice}
                        type="text"
                        label="newPrice"
                        input="newPrice"
                        onChange={(e) => {
                            let value = e.target.value;

                            // Allow empty
                            if (value === '') {
                                setNewDefaultPrice('');
                                return;
                            }

                            // If the user types 0 followed by a digit (not a dot), auto-add dot
                            if (/^0\d/.test(value)) {
                                value = '0.' + value.substring(1);
                            }

                            // Validate format: digits, optional dot, max 4 digits after dot
                            const regex = /^\d*\.?\d{0,4}$/;
                            if (regex.test(value)) {
                                setNewDefaultPrice(value);
                            }
                        }}
                        hasError={false}
                        helperText=""
                    />

                    <Box sx={{display: 'flex', gap: 2, justifyContent: 'flex-end', marginTop: 2}}>
                        <Button
                            variant="outlined"
                            onClick={handleClosePriceModal}
                        >
                            <FormattedMessage id="cancel"/>
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleUpdateDefaultPrice}
                            disabled={newDefaultPrice === '' || parseFloat(newDefaultPrice) < 0 || isNaN(parseFloat(newDefaultPrice))}
                        >
                            <FormattedMessage id="update"/>
                        </Button>
                    </Box>
                </Box>
            </AMPModal>
        </>
    );
};